import { API_BASE } from '../apiBase';
// ─────────────────────────────────────────────────────────────────────────────
// Physical restraint audit worksheet — API client.
//
// The COMPILED half is read-only: it is built by the nightly MDS scan and stored, and
// there is deliberately no "compile now" — that would cost three PointClickCare reads
// per resident per page load.
//
// The writes here are all things a PERSON does: opening a review, answering a row,
// signing off, and asking the engine to read the chart. The chart pull costs no
// PointClickCare call (the notes are already in Supabase) but it is a model call, so it
// is a button rather than something that happens on load.
//
// A 403 is a NORMAL ANSWER here, not an error. The feature is gated on purpose,
// so it is returned as a locked state for the panel to render rather than thrown
// as a failure for an error boundary to catch.
// ─────────────────────────────────────────────────────────────────────────────

const BASE = API_BASE + "/api/survai-engine";

async function authHeaders() {
  try {
    const { supabase } = await import("../supabaseClient");
    if (!supabase) return {};
    const { data } = await supabase.auth.getSession();
    const token = data?.session?.access_token;
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch {
    return {};
  }
}

/**
 * Fetch the stored worksheet for a facility.
 *
 * @param {string} ccn
 * @param {object} [opts]
 * @param {boolean} [opts.all]  include residents with no restraint coded
 * @returns {Promise<{locked?:boolean, reason?:string, data?:object}>}
 */
/**
 * Every call goes through here so the gated case is handled once.
 *
 * A 403 is a NORMAL ANSWER — the feature is in preview on purpose — so it comes back
 * as `{ locked }` for the panel to render. Everything else throws.
 */
async function send(method, path, body) {
  const res = await fetch(BASE + path, {
    method,
    headers: { "Content-Type": "application/json", ...(await authHeaders()) },
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  if (res.status === 403 || res.status === 401) {
    let reason = "";
    try {
      const j = await res.json();
      reason = j?.error?.message || "";
    } catch {
      /* the message is a nicety; the locked state is the fact */
    }
    return { locked: true, reason };
  }
  if (!res.ok) {
    let detail = res.statusText;
    let code = null;
    let details = null;
    try {
      const j = await res.json();
      detail = j?.error?.message || detail;
      // THE CODE AND THE DETAILS, CARRIED. Without them a caller can only match on
      // English: the 409 for "an unfinished audit of a DIFFERENT event is in the way"
      // is a state the screen must handle differently from a generic failure, and its
      // details hold WHICH event is open. Branching on a message is how that breaks the
      // next time the sentence is reworded.
      code = j?.error?.code ?? null;
      details = j?.error?.details ?? null;
    } catch {
      /* ignore */
    }
    const err = new Error(detail);
    err.status = res.status;
    if (code) err.code = code;
    if (details) err.details = details;
    throw err;
  }
  if (res.status === 204) return { data: null };
  const json = await res.json();
  return { data: json?.data ?? null };
}

const get = (path) => send("GET", path);

export async function fetchPostIncidentAudits(ccn, { all = false, residentId = null, aspect = null } = {}) {
  const qs = new URLSearchParams();
  if (all) qs.set("scope", "all");
  if (residentId) qs.set("residentId", residentId);
  // THE ASPECT SWITCHER WAS DECORATIVE UNTIL THIS LINE. The chips highlighted and the
  // aspect never left the browser, so every request fetched restraints — which was
  // harmless while restraints was the only worksheet and would have shown a nurse the
  // wrong aspect's evidence the moment a second one existed.
  if (aspect) qs.set("aspect", aspect);
  const q = qs.toString();
  return get(`/facilities/${encodeURIComponent(ccn)}/post-incident-audit${q ? `?${q}` : ""}`);
}

/**
 * One resident's worksheet: the compiled half, the review, and the merged rows.
 */
export async function fetchResidentWorksheet(ccn, residentId, { aspect = null } = {}) {
  const q = aspect ? `?aspect=${encodeURIComponent(aspect)}` : "";
  return get(`/facilities/${encodeURIComponent(ccn)}/post-incident-audit/${encodeURIComponent(residentId)}${q}`);
}

/**
 * Open an audit, or resume the one already open.
 *
 * Idempotent on the server, so the button does not need to guard against a double
 * press or a second nurse. `created` says which happened, so the screen can say
 * "resuming" rather than "started" — a nurse told they started something that was
 * already half done will fill it in twice.
 */
export async function startReview(
  ccn,
  residentId,
  { triggerKind = "ROUTINE", triggerRef = null, aspect = null, incidentId = null } = {},
) {
  return send("POST", `/facilities/${encodeURIComponent(ccn)}/post-incident-audit/${encodeURIComponent(residentId)}/review`, {
    triggerKind,
    triggerRef,
    // WHICH EVENT THIS AUDIT IS OF. Migration 066 keys the open-review slot on it, so
    // this is what lets a resident's two falls have two audits instead of the second
    // silently resuming the first and filing the 3rd's answers under the 28th.
    //
    // A 409 `audit_open_for_another_incident` comes back when an unfinished audit of a
    // DIFFERENT event is in the way; its details carry openIncidentId so the screen can
    // name it.
    ...(incidentId ? { incidentId } : {}),
    // WITHOUT THIS, EVERY FALLS AUDIT OPENED A RESTRAINTS REVIEW. The aspect is part of
    // 028's open-review key, so a review started from the falls worksheet and stored as
    // RESTRAINTS would collide with that resident's restraint audit and answer the wrong
    // eleven rows.
    ...(aspect ? { aspect } : {}),
  });
}

/**
 * Save answers, header fields, and optionally sign off — one call for the whole
 * worksheet, because that is how a worksheet is filled in.
 */
export async function saveReview(ccn, reviewId, body) {
  return send("PUT", `/facilities/${encodeURIComponent(ccn)}/post-incident-audit/review/${encodeURIComponent(reviewId)}`, body);
}

/** This resident's incidents, so a person can say which event the audit follows. */
export async function fetchIncidents(ccn, residentId) {
  return get(`/facilities/${encodeURIComponent(ccn)}/post-incident-audit/${encodeURIComponent(residentId)}/incidents`);
}

/**
 * Ask the engine to read the chart against the worksheet.
 *
 * Costs no PointClickCare call — the notes and incidents are already in Supabase — but
 * it IS a model call, so it is a button a person presses rather than something that
 * happens on every load.
 */
export async function pullFromChart(ccn, reviewId, { incidentId = null } = {}) {
  return send("POST", `/facilities/${encodeURIComponent(ccn)}/post-incident-audit/review/${encodeURIComponent(reviewId)}/pull`, {
    incidentId,
  });
}

/** Accept a proposal. The engine writes the answer as THIS person's entry. */
export async function acceptSuggestion(ccn, id) {
  return send("POST", `/facilities/${encodeURIComponent(ccn)}/post-incident-audit/suggestion/${encodeURIComponent(id)}/accept`);
}

/** Dismiss a proposal. Recorded, not deleted, so it does not come back. */
export async function dismissSuggestion(ccn, id, reason = null) {
  return send("POST", `/facilities/${encodeURIComponent(ccn)}/post-incident-audit/suggestion/${encodeURIComponent(id)}/dismiss`, {
    reason,
  });
}

/**
 * THE 24-HOUR CHART CHECK — every resident with activity in the window.
 *
 * Here rather than in its own client because the auth header, the 403-is-a-normal-answer
 * rule and the error-code carrying above are the parts that are easy to get subtly wrong,
 * and the endpoint sits on the same router behind the same gate. A second copy of `send`
 * would be a second place for the locked state to be mishandled.
 *
 * Costs no PointClickCare call: both readable legs come out of our own mirror, written by
 * the nightly scan. The response says how stale that is — see `freshness` and `baseline`.
 */
export async function fetchChartCheck(ccn, { hours = null } = {}) {
  const q = hours ? `?hours=${encodeURIComponent(hours)}` : "";
  return get(`/facilities/${encodeURIComponent(ccn)}/chart-check${q}`);
}

/**
 * "I reviewed this chart", and taking it back.
 *
 * The window goes with BOTH, and the server clamps it: an un-mark is a delete, and without a
 * server-resolved window `hours=100000` would turn "undo my tick" into "remove this
 * resident's whole history".
 *
 * A tick records that a person looked and NOTHING else — no state, no findings, no effect on
 * any percentage. The post-incident worksheet is where an accountable answer goes.
 */
/**
 * Tick a chart, optionally with what was DONE about it.
 *
 * `disposition` is NOTE_ADDED | NOT_REQUIRED | REFERRED, and it is optional: a bare tick
 * still means "a person looked", which is what every mark made before this feature existed
 * means. Sent only when one was chosen, so a plain tick names no column — the server rejects
 * an unknown disposition with a 400 and a sentence, and keeps the tick either way.
 */
export async function markChartReviewed(ccn, residentId, { hours = null, disposition = null, dispositionNote = null } = {}) {
  const q = hours ? `?hours=${encodeURIComponent(hours)}` : "";
  const body = disposition ? { disposition, ...(dispositionNote ? { dispositionNote } : {}) } : undefined;
  return send("POST", `/facilities/${encodeURIComponent(ccn)}/chart-check/${encodeURIComponent(residentId)}/reviewed${q}`, body);
}

export async function unmarkChartReviewed(ccn, residentId, { hours = null } = {}) {
  const q = hours ? `?hours=${encodeURIComponent(hours)}` : "";
  return send("DELETE", `/facilities/${encodeURIComponent(ccn)}/chart-check/${encodeURIComponent(residentId)}/reviewed${q}`);
}

export default fetchPostIncidentAudits;
